import { createSlice } from '@reduxjs/toolkit';

export interface  LoginDescriptor {
	username: string;
	password: string;
	api_key?: string;
	owner?: string;
	max_projects?: number;
	role?: string;
	name?: string;
	email?: string;
	is_new?: boolean;
	to_delete?: boolean;
  privileges: string[];
  // Admin override. When true, 2FA is OFF for this user — they skip the
  // forced-enrollment screen and the login-time code prompt. Default
  // (absent/false) means standard 2FA policy applies. PERSISTED on the Go
  // side (logins.json).
  totp_disabled?: boolean;
  // TRANSIENT — server-computed from the presence of a TotpRecord. Never
  // persisted to logins.json; only appears on API responses. The React UI
  // uses this to decide between EnrollTwoFA and Home for enrolled users.
  totp_enrolled?: boolean;
  supports_2fa?: boolean;
  local_project_id?: string;
  email_verified?:				boolean;
  email_verified_at?:				number;
  pending_email?:					string;
  pending_email_expires_at?:		number;
  preferred_2fa_method?:			'totp' | 'email';
}

// Response shape from /login.json when the user has 2FA enabled.
// Wraps LoginDescriptor with the extra fields the second step needs.
export interface LoginStep1Response extends LoginDescriptor {
  requires_2fa?: boolean;
  ticket?: string;
  ticket_expires_at?: string;
  methods?: string[];
}

// Body for POST /login/2fa.json.
export interface Login2FARequest {
  ticket: string;
  code: string;
  method?: string;
}

// Response from /login/2fa.json on success — same as LoginDescriptor plus
// an optional remaining-recovery-codes counter when a recovery code was used.
export interface Login2FAResponse extends LoginDescriptor {
  recovery_codes_remaining?: number;
}

// One enrolled authenticator. Returned by GET /2fa.json?action=devices with
// no secret material; identified by a server-generated UUID.
export interface TotpDeviceInfo {
  id: string;
  name: string;
  added_at: number;
  last_used_at?: number;
}

export interface LoginPrivilegesId{
  id:string
}
export interface LoginPrivileges{
  id:string,
  desc:string
}

export interface CategoryPrivileges {
  category: string;
  privileges: LoginPrivileges[];
}

export type CategorizedPrivileges = CategoryPrivileges[];

export interface LoginStruct {
	state: string;
	descriptors: LoginDescriptor[];
  privileges: CategorizedPrivileges;

}

export interface LoginInfo {
  username:				string;
  password:				string;
  remember_me?:			boolean;
  // Capability flag — frontend MUST send true so the backend enforces the
  // 2FA challenge. Omitting it (or sending false) triggers the temporary
  // backward-compat bypass shim that ships in backend 0.7.11/0.7.12.
  supports_2fa?:		boolean;
}

// 2FA challenge state populated after a successful step-1 login when the
// user is enrolled (server-side check). The Login page reads these to
// switch from username/password entry to a code-entry view, then clears
// them on success (or on the user cancelling).
const initialState = {
  error:			"",
  status:			-1,
  login:			null as LoginDescriptor | null,
  logins:			{	state:			"",
						descriptors:	[],
            privileges: []
					} as LoginStruct,
  success:			"",
  // 2FA fields:
  requires2FA:                  false,
  ticket:                       "",
  ticketExpiresAt:              "",
  methods:                      [] as string[],
  recoveryCodesRemaining:       null as number | null,
  supports2FA:                  false,
  // Step-2 lockout: RFC3339 timestamp from the server's 401 lockout
  // response (5 failed attempts in 15 min). Login reads this to disable
  // the code input and render a countdown. Cleared once the timestamp
  // is in the past OR the user successfully verifies / cancels.
  lockedUntil:                  "" as string,
};

export const loginsSlice = createSlice({
  name: 'logins',
  initialState: initialState,
  reducers: {
    assign: (state, action) => {
      state.error = action.payload.error;
      state.status = action.payload.status;
      state.login = action.payload.login;
      state.logins = action.payload.logins;
      state.success = action.payload.success;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLogin: (state, action) => {
      state.login = action.payload;
    },
    setLogins: (state, action) => {
      state.logins = action.payload;

      if (state.logins)
        state.logins.descriptors.sort(
          (a, b) => {
            const au = a.username.toLowerCase();
            const bu = b.username.toLowerCase();

            return	au == bu
					?	0
					:	(	au < bu
							?	-1
							:	1
						);
		  }
		);
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setSupports2FA: (state, action) => {
      state.supports2FA = action.payload === true;
    },
    set2FAChallenge: (state, action) => {
      state.requires2FA = !!action.payload.requires_2fa;
      state.ticket = action.payload.ticket || "";
      state.ticketExpiresAt = action.payload.ticket_expires_at || "";
      state.methods = action.payload.methods || [];
    },
    setRecoveryCodesRemaining: (state, action) => {
      state.recoveryCodesRemaining = action.payload;
    },
    setLockout: (state, action) => {
      state.lockedUntil = action.payload || "";
    },
    clearLockout: (state) => {
      state.lockedUntil = "";
    },
    clear2FAChallenge: (state) => {
      state.requires2FA = false;
      state.ticket = "";
      state.ticketExpiresAt = "";
      state.methods = [];
      state.lockedUntil = "";
    },
  },
});

export const loginsUrl = "/logins.json";
export const loginUrl = "/login.json";
export const login2faUrl = "/login/2fa.json";

export function copyLoginDescriptor(desc: LoginDescriptor): LoginDescriptor {
  return {
    username:				desc.username,
	password:				desc.password,
	api_key:				desc.api_key,
	owner:					desc.owner,
	max_projects:			desc.max_projects,
	role:					desc.role,
	name:					desc.name,
	email:					desc.email,
	is_new:					desc.is_new,
	to_delete:				desc.to_delete,
  privileges:     desc.privileges ? [...desc.privileges] : [],
  // Carry the transient fields through immutable copies — dropping any of
  // them silently breaks user flows (totp_disabled: admin Enable/Disable
  // toast; totp_enrolled: EnrollTwoFA vs Home routing; local_project_id:
  // local-user header rendering / scope-aware routing).
  totp_disabled:    desc.totp_disabled,
  totp_enrolled:    desc.totp_enrolled,
  supports_2fa:     desc.supports_2fa,
  local_project_id: desc.local_project_id,
  email_verified:				desc.email_verified,
  email_verified_at:			desc.email_verified_at,
  pending_email:				desc.pending_email,
  pending_email_expires_at:	desc.pending_email_expires_at,
  preferred_2fa_method:		desc.preferred_2fa_method,
  };
}

export function copyLoginStruct(ls: LoginStruct): LoginStruct {
  const copiedPrivileges: CategorizedPrivileges = (ls.privileges || []).map((cat) => ({
    category: cat.category,
    privileges: cat.privileges.map((p) => ({ ...p }))
  }));
  return {
    state:					ls.state,
    descriptors:			ls.descriptors.map((desc) => copyLoginDescriptor(desc)),
    privileges:   copiedPrivileges

  };
}

export function getNewLogin(): LoginDescriptor {
  return {
    username:				"",
	password:				"",
	role:					"",
	name:					"",
	api_key:				"",
	owner:					"",
	max_projects:			0,
	is_new:					true,
	privileges:				[],
  };
}

export function getLoginFromStruct(username: string, ls: LoginStruct): LoginDescriptor | null {
  for(const login of ls.descriptors) {
    if (login.username != username)
      continue;

    return copyLoginDescriptor(login);
  }

  return null;
}

export function flattenPrivileges(privileges: CategorizedPrivileges): LoginPrivileges[] {
  const result: LoginPrivileges[] = [];
  if (privileges && Array.isArray(privileges)) {
    for (const cat of privileges) {
      result.push(...cat.privileges);
    }
  }
  return result;
}

export function getPrivilegeById(privileges: CategorizedPrivileges, id: string): LoginPrivileges | undefined {
  if (!privileges || !Array.isArray(privileges)) return undefined;
  for (const cat of privileges) {
    const found = cat.privileges.find((p: LoginPrivileges) => p.id === id);
    if (found) return found;
  }
  return undefined;
}
